import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { requestJson } from '../lib/api';
import { SITE_SESSION_STORAGE_KEY } from './SiteSessionContext';

export const AUTH_STORAGE_KEY = 'opticdev.authSession';

function readStoredAuth() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuthState] = useState(readStoredAuth);

  const setAuth = useCallback((nextAuth) => {
    setAuthState(nextAuth);
    if (nextAuth) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(SITE_SESSION_STORAGE_KEY);
    }
  }, []);

  const login = useCallback(async ({ email, password, siteId, logisticsUser, login: collaboratorLogin }) => {
    const payload = await requestJson(logisticsUser ? '/auth/logistics-login' : '/auth/login', {
      method: 'POST',
      body: JSON.stringify(logisticsUser ? { login: collaboratorLogin, password } : siteId ? { siteId, password } : { email, password }),
    });
    setAuth(payload);
    return payload;
  }, [setAuth]);

  const register = useCallback((form) =>
    requestJson('/auth/register', {
      method: 'POST',
      body: JSON.stringify(form),
    }), []);

  const confirmEmail = useCallback(async (token) => {
    const payload = await requestJson('/auth/confirm-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    setAuth(payload);
    return payload;
  }, [setAuth]);

  const resendConfirmation = useCallback((email) =>
    requestJson('/auth/resend-confirmation', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }), []);

  const logout = useCallback(() => setAuth(null), [setAuth]);

  const value = useMemo(
    () => ({
      auth,
      account: auth?.account || null,
      token: auth?.token || null,
      isAuthenticated: Boolean(auth?.token && auth?.account?.emailVerified),
      login,
      register,
      confirmEmail,
      resendConfirmation,
      logout,
    }),
    [auth, confirmEmail, login, logout, register, resendConfirmation]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
